import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useAssetsStore } from '@/stores/assetsStore';
import { useRisks, useTasks, useEvidence } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { RiskDialog } from './RiskDialog';
import { TaskDialog } from './TaskDialog';
import { EvidenceDialog } from './EvidenceDialog';
import { NodeEditDialog } from './NodeEditDialog';
import { LinkManager } from '@/components/shared/LinkManager';
import { 
  Plus,
  CheckCircle2, 
  Shield,
  Trash2,
  Edit
} from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { toast } from 'sonner';
import type { Task, TaskStatus } from '@/hooks/queries/useTasks';
import type { RiskSeverity, TaskRecord, TaskCadence } from '@/types/assets';

dayjs.extend(relativeTime);

interface InspectorDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InspectorDrawer({ open, onOpenChange }: InspectorDrawerProps) {
  const { 
    selectedNodeId, 
    nodes, 
    deleteNode,
    setSelectedNode
  } = useAssetsStore();

  const { organization } = useAuth();
  const { risks, deleteRisk, calculateRiskScore } = useRisks(organization?.id);
  const { tasks, deleteTask, completeTask } = useTasks(organization?.id);
  const { evidence, deleteEvidence } = useEvidence(organization?.id);

  const [riskDialogOpen, setRiskDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [evidenceDialogOpen, setEvidenceDialogOpen] = useState(false);
  const [nodeEditDialogOpen, setNodeEditDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskRecord | null>(null);

  // Load data when drawer opens
  useEffect(() => {
    if (open) {
      // Data is now loaded via react-query hooks automatically
    }
  }, [open]);

  const selectedNode = selectedNodeId ? nodes.find(n => n.id === selectedNodeId) : null;
  const nodeRisks = selectedNodeId && risks ? risks.filter(r => r.related_to_id === selectedNodeId && r.related_to_type === 'asset') : [];
  const nodeTasks = selectedNodeId && tasks ? tasks.filter(t => t.related_to_id === selectedNodeId && t.related_to_type === 'asset') : [];
  const nodeEvidence = selectedNodeId && evidence ? evidence.filter(e => e.related_to_id === selectedNodeId && e.related_to_type === 'asset') : [];
  
  const toTaskRecord = (task: Task): TaskRecord => {
    const cadence = normalizeCadence(task.cadence);
    return {
      id: task.id,
      nodeId: task.related_to_id || selectedNodeId || '',
      title: task.title,
      description: task.description || undefined,
      cadence,
      rrule: task.rrule || undefined,
      owner: task.owner || undefined,
      due_next: task.due_next,
      last_run: undefined,
      status: task.status,
      evidence_ids: [],
      tags: [],
    };
  };

  const normalizeCadence = (cadence: string): TaskCadence => {
    if (
      cadence === 'once' ||
      cadence === 'daily' ||
      cadence === 'weekly' ||
      cadence === 'monthly' ||
      cadence === 'quarterly' ||
      cadence === 'yearly' ||
      cadence === 'custom_rrule'
    ) {
      return cadence;
    }
    return 'monthly';
  };

  if (!selectedNode) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-96">
          <SheetHeader>
            <SheetTitle>No Node Selected</SheetTitle>
          </SheetHeader>
          <p className="text-muted-foreground">Select a node to view its details.</p>
        </SheetContent>
      </Sheet>
    );
  }

  const getRiskSeverityColor = (severity: RiskSeverity) => {
    switch (severity) {
      case 'Low':
        return 'bg-green-500/10 text-green-700 border-green-500/20';
      case 'Medium':
        return 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20';
      case 'High':
        return 'bg-orange-500/10 text-orange-700 border-orange-500/20';
      case 'Critical':
        return 'bg-red-500/10 text-red-700 border-red-500/20';
      default:
        return 'bg-gray-500/10 text-gray-700 border-gray-500/20';
    }
  };

  const getTaskStatusColor = (status: TaskStatus) => {
    switch (status) {
      case 'Scheduled':
        return 'bg-blue-500/10 text-blue-700 border-blue-500/20';
      case 'Due':
        return 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20';
      case 'Overdue':
        return 'bg-red-500/10 text-red-700 border-red-500/20';
      case 'Done':
        return 'bg-green-500/10 text-green-700 border-green-500/20';
      default:
        return 'bg-gray-500/10 text-gray-700 border-gray-500/20';
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-96 overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {selectedNode.data.label}
            </SheetTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setNodeEditDialogOpen(true)}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Node</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete "{selectedNode.data.label}"? This action cannot be undone and will also remove all associated risks, tasks, and evidence.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={async () => {
                        try {
                          await deleteNode(selectedNodeId!);
                          setSelectedNode(null);
                          onOpenChange(false);
                          toast.success('Node deleted successfully');
                        } catch (error) {
                          toast.error('Failed to delete node');
                        }
                      }}
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </SheetHeader>

        <div className="space-y-4 mt-4">
          {/* Node Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Type:</span>
                <span className="text-sm font-medium capitalize">{selectedNode.data.type}</span>
              </div>
              {selectedNode.data.domain && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Domain:</span>
                  <Badge variant="outline">{selectedNode.data.domain}</Badge>
                </div>
              )}
              {selectedNode.data.purdue_level && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Purdue Level:</span>
                  <Badge variant="outline">{selectedNode.data.purdue_level}</Badge>
                </div>
              )}
              {selectedNode.data.meta?.ip && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">IP:</span>
                  <span className="text-sm font-mono">{selectedNode.data.meta.ip}</span>
                </div>
              )}
              {selectedNode.data.meta?.model && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Model:</span>
                  <span className="text-sm">{selectedNode.data.meta.model}</span>
                </div>
              )}
              {selectedNode.data.meta?.vendor && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Vendor:</span>
                  <span className="text-sm">{selectedNode.data.meta.vendor}</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Tabs defaultValue="risks" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="risks" className="text-xs">
                Risks ({nodeRisks.length})
              </TabsTrigger>
              <TabsTrigger value="tasks" className="text-xs">
                Tasks ({nodeTasks.length})
              </TabsTrigger>
              <TabsTrigger value="evidence" className="text-xs">
                Evidence ({nodeEvidence.length})
              </TabsTrigger>
              <TabsTrigger value="relationships" className="text-xs">
                Links
              </TabsTrigger>
            </TabsList>

            <TabsContent value="risks" className="space-y-2">
              <div className="flex justify-between items-center">
                <h4 className="font-medium">Risks</h4>
                <Button 
                  size="sm" 
                  onClick={() => setRiskDialogOpen(true)}
                  className="h-7"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              </div>
              
              {nodeRisks.length === 0 ? (
                <p className="text-sm text-muted-foreground">No risks identified</p>
              ) : (
                <div className="space-y-2">
                  {nodeRisks.map((risk) => {
                    const { score, severity } = calculateRiskScore(
                      risk.likelihood || 3, 
                      risk.impact || 3
                    );
                    return (
                      <Card key={risk.id} className="p-3">
                        <div className="flex justify-between items-start mb-2">
                          <h5 className="text-sm font-medium">{risk.title}</h5>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteRisk(risk.id)}
                            className="h-6 w-6 p-0"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">
                          {risk.cause || risk.consequence}
                        </p>
                        <div className="flex justify-between items-center">
                          <Badge 
                            variant="outline" 
                            className={getRiskSeverityColor(severity as RiskSeverity)}
                          >
                            {severity} ({score})
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setRiskDialogOpen(true);
                            }}
                            className="h-6 text-xs"
                          >
                            Edit
                          </Button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="tasks" className="space-y-2">
              <div className="flex justify-between items-center">
                <h4 className="font-medium">Tasks</h4>
                <Button 
                  size="sm" 
                  onClick={() => setTaskDialogOpen(true)}
                  className="h-7"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              </div>
              
              {nodeTasks.length === 0 ? (
                <p className="text-sm text-muted-foreground">No tasks scheduled</p>
              ) : (
                <div className="space-y-2">
                  {nodeTasks.map((task) => (
                    <Card key={task.id} className="p-3">
                      <div className="flex justify-between items-start mb-2">
                        <h5 className="text-sm font-medium">{task.title}</h5>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteTask(task.id)}
                          className="h-6 w-6 p-0"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Badge 
                            variant="outline" 
                            className={getTaskStatusColor(task.status)}
                          >
                            {task.status}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {task.cadence}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Due: {dayjs(task.due_next).fromNow()}
                        </div>
                        <div className="flex gap-1">
                          {task.status !== 'Done' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => completeTask(task.id)}
                              className="h-6 text-xs"
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Complete
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingTask(toTaskRecord(task));
                              setTaskDialogOpen(true);
                            }}
                            className="h-6 text-xs"
                          >
                            Edit
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="evidence" className="space-y-2">
              <div className="flex justify-between items-center">
                <h4 className="font-medium">Evidence</h4>
                <Button 
                  size="sm" 
                  onClick={() => setEvidenceDialogOpen(true)}
                  className="h-7"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              </div>
              
              {nodeEvidence.length === 0 ? (
                <p className="text-sm text-muted-foreground">No evidence attached</p>
              ) : (
                <div className="space-y-2">
                  {nodeEvidence.map((item) => (
                    <Card key={item.id} className="p-3">
                      <div className="flex justify-between items-start mb-2">
                        <h5 className="text-sm font-medium">{item.name}</h5>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteEvidence(item.id)}
                          className="h-6 w-6 p-0"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="space-y-1">
                        <Badge variant="outline">{item.type}</Badge>
                        <div className="text-xs text-muted-foreground">
                          {dayjs(item.created_at).fromNow()}
                        </div>
                        {item.notes && (
                          <p className="text-xs">{item.notes}</p>
                        )}
                        {item.storage_path && (
                          <a 
                            href={item.storage_path} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline"
                          >
                            Open Link
                          </a>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="relationships" className="space-y-4">
              <LinkManager
                entityType="asset"
                entityId={selectedNodeId!}
                entityName={selectedNode.data.label}
                allowedTypes={['site', 'zone', 'system', 'risk', 'control', 'supplier', 'incident', 'task', 'evidence']}
                showHeader={false}
              />
              
              <div className="pt-4">
                <h4 className="text-sm font-medium mb-3">Related Suppliers</h4>
                <LinkManager
                  entityType="asset"
                  entityId={selectedNodeId!}
                  entityName={selectedNode.data.label}
                  allowedTypes={['supplier']}
                  showHeader={false}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Dialogs */}
        <RiskDialog
          open={riskDialogOpen}
          onOpenChange={setRiskDialogOpen}
          nodeId={selectedNodeId!}
          relatedType="asset"
        />

        <TaskDialog
          open={taskDialogOpen}
          onOpenChange={(open) => {
            setTaskDialogOpen(open);
            if (!open) setEditingTask(null);
          }}
          nodeId={selectedNodeId!}
          task={editingTask ?? undefined}
        />

        <EvidenceDialog
          open={evidenceDialogOpen}
          onOpenChange={setEvidenceDialogOpen}
          nodeId={selectedNodeId!}
          relatedType="asset"
        />

        <NodeEditDialog
          open={nodeEditDialogOpen}
          onOpenChange={setNodeEditDialogOpen}
          nodeId={selectedNodeId}
        />
      </SheetContent>
    </Sheet>
  );
}